import { CONFIG } from '../config';
import { Bullet } from '../entities/Bullet';

interface RendererLike {
    getCanvasWidth(): number;
    getCanvasHeight(): number;
    isLandscape: boolean;
}

interface DifficultyMultipliers {
    SPAWN_INTERVAL_MULT: number;
    BULLET_SPEED_MULT: number;
    RAMP_TIME_MULT: number;
}

interface CanvasSize {
    width: number;
    height: number;
    isLandscape: boolean;
}

type BulletPattern = 'straight' | 'angled' | 'multi' | 'circular';

/**
 * Bullet 엔티티 풀 - new Bullet() 호출 최소화
 */
class BulletEntityPool {
    private pool: Bullet[] = [];
    // iOS: 풀 크기 제한
    private maxPoolSize: number = /iPad|iPhone|iPod/.test(navigator.userAgent) ? 100 : 300;

    get(x: number, y: number, vx: number, vy: number, color?: string, canSplit: boolean = false): Bullet {
        const bullet = this.pool.pop();
        if (bullet) {
            bullet.reset(x, y, vx, vy, color, canSplit);
            return bullet;
        }
        return new Bullet(x, y, vx, vy, color, canSplit);
    }

    release(bullet: Bullet): void {
        if (this.pool.length < this.maxPoolSize) {
            this.pool.push(bullet);
        }
        // 풀 크기 초과 시 그냥 버림 (GC가 수거)
    }

    clear(): void {
        this.pool = [];
    }

    get poolSize(): number {
        return this.pool.length;
    }
}

export class BulletSpawner {
    private bullets: Bullet[];
    private bulletPool: BulletEntityPool;
    private timeSinceSpawn: number;
    private gameTime: number;
    private soundManager: unknown;
    private renderer: RendererLike | null;
    private difficultyMultipliers: DifficultyMultipliers;
    private bigballMode: boolean;

    constructor(soundManager: unknown = null) {
        this.bullets = [];
        this.bulletPool = new BulletEntityPool();
        this.timeSinceSpawn = 0;
        this.gameTime = 0;
        this.soundManager = soundManager;
        this.renderer = null;
        this.difficultyMultipliers = {
            SPAWN_INTERVAL_MULT: 1.0,
            BULLET_SPEED_MULT: 1.0,
            RAMP_TIME_MULT: 1.0
        };
        this.bigballMode = false;
    }

    setDifficultyMultipliers(multipliers: DifficultyMultipliers): void {
        this.difficultyMultipliers = multipliers;
    }

    setBigballMode(enabled: boolean): void {
        this.bigballMode = enabled;
    }

    setRenderer(renderer: RendererLike): void {
        this.renderer = renderer;
    }

    getCanvasSize(): CanvasSize {
        if (this.renderer) {
            return {
                width: this.renderer.getCanvasWidth(),
                height: this.renderer.getCanvasHeight(),
                isLandscape: this.renderer.isLandscape
            };
        }
        return {
            width: CONFIG.CANVAS_WIDTH,
            height: CONFIG.CANVAS_HEIGHT,
            isLandscape: false
        };
    }

    reset(): void {
        // 모든 총알을 풀로 반환
        for (const bullet of this.bullets) {
            this.bulletPool.release(bullet);
        }
        this.bullets = [];
        this.timeSinceSpawn = 0;
        this.gameTime = 0;
    }

    update(deltaTime: number, bulletSpeedMult: number = 1.0): void {
        this.gameTime += deltaTime;
        this.timeSinceSpawn += deltaTime;

        // Calculate current spawn interval based on difficulty
        const spawnInterval = this.getSpawnInterval();

        // Spawn new bullets
        if (this.timeSinceSpawn >= spawnInterval) {
            this.spawnBullet();
            this.timeSinceSpawn = 0;
        }

        // Update all bullets
        this.updateBulletsOnly(deltaTime, bulletSpeedMult);
    }

    updateBulletsOnly(deltaTime: number, bulletSpeedMult: number = 1.0): void {
        const { width, height } = this.getCanvasSize();
        // Update all bullets without spawning new ones
        // swap-and-pop 패턴으로 배열 조작 최적화
        let writeIdx = 0;
        for (let i = 0; i < this.bullets.length; i++) {
            const bullet = this.bullets[i];
            bullet.update(deltaTime * bulletSpeedMult, width, height);

            if (bullet.isAlive) {
                this.bullets[writeIdx++] = bullet;
            } else {
                // 죽은 총알은 풀로 반환
                this.bulletPool.release(bullet);
            }
        }
        this.bullets.length = writeIdx;
    }

    /**
     * 모든 총알 제거 (폭탄 파워업용)
     */
    clearAllBullets(): void {
        // 모든 총알을 풀로 반환
        for (const bullet of this.bullets) {
            this.bulletPool.release(bullet);
        }
        this.bullets = [];
    }

    getSpawnInterval(): number {
        const rampTime = CONFIG.DIFFICULTY.RAMP_TIME * this.difficultyMultipliers.RAMP_TIME_MULT;
        const progress = Math.min(this.gameTime / rampTime, 1);
        const baseInterval = CONFIG.DIFFICULTY.SPAWN_INTERVAL_START -
            progress * (CONFIG.DIFFICULTY.SPAWN_INTERVAL_START - CONFIG.DIFFICULTY.SPAWN_INTERVAL_MIN);
        return baseInterval * this.difficultyMultipliers.SPAWN_INTERVAL_MULT;
    }

    getBulletSpeed(): number {
        const rampTime = CONFIG.DIFFICULTY.RAMP_TIME * this.difficultyMultipliers.RAMP_TIME_MULT;
        const progress = Math.min(this.gameTime / rampTime, 1);
        const baseSpeed = CONFIG.BULLET.SPEED_MIN +
            progress * (CONFIG.BULLET.SPEED_MAX - CONFIG.BULLET.SPEED_MIN);

        // 더 많은 랜덤성 추가: 기본 속도의 ±30%
        const randomFactor = 0.7 + Math.random() * 0.6; // 0.7 ~ 1.3
        return baseSpeed * randomFactor * this.difficultyMultipliers.BULLET_SPEED_MULT;
    }

    spawnBullet(): void {
        const pattern = this.getCurrentPattern();
        const speed = this.getBulletSpeed();

        switch (pattern) {
            case 'straight':
                this.spawnStraightBullet(speed);
                break;
            case 'angled':
                this.spawnAngledBullet(speed);
                break;
            case 'multi':
                this.spawnMultiDirectionalBullet(speed);
                break;
            case 'circular':
                this.spawnCircularPattern(speed);
                break;
        }
    }

    getCurrentPattern(): BulletPattern {
        if (this.gameTime < CONFIG.DIFFICULTY.PATTERN_SIMPLE_MAX) {
            return 'straight';
        } else if (this.gameTime < CONFIG.DIFFICULTY.PATTERN_MEDIUM_MAX) {
            // 처음부터 다양한 방향 - multi 패턴을 랜덤으로 섞음
            return Math.random() < 0.5 ? 'angled' : 'multi';
        } else if (this.gameTime < CONFIG.DIFFICULTY.PATTERN_HARD_MAX) {
            return 'multi';
        } else {
            return 'circular';
        }
    }

    // Helper method to add bullet
    addBullet(bullet: Bullet): void {
        if (this.bigballMode) {
            bullet.radius = CONFIG.BULLET.RADIUS * CONFIG.BIGBALL.BULLET_SIZE_MULT;
            bullet.vx *= CONFIG.BIGBALL.SPEED_MULT;
            bullet.vy *= CONFIG.BIGBALL.SPEED_MULT;
        }
        this.bullets.push(bullet);
    }

    spawnStraightBullet(speed: number): void {
        const { width, height, isLandscape } = this.getCanvasSize();
        let x: number, y: number, vx: number, vy: number;

        if (isLandscape) {
            // Landscape: spawn from right, move left
            x = width + 20;
            y = Math.random() * height;
            vx = -speed;
            vy = 0;
        } else {
            // Portrait: spawn from top, move down
            x = Math.random() * width;
            y = -20;
            vx = 0;
            vy = speed;
        }

        this.addBullet(this.bulletPool.get(x, y, vx, vy));
    }

    spawnAngledBullet(speed: number): void {
        const { width, height, isLandscape } = this.getCanvasSize();
        const side = Math.random() < 0.5 ? 'top' : 'bottom';
        let x: number, y: number, vx: number, vy: number;

        if (isLandscape) {
            // Landscape: spawn from right with angle
            x = width + 20;
            if (side === 'top') {
                y = Math.random() * (height * 0.3);
                vy = speed * 0.3;
            } else {
                y = height - Math.random() * (height * 0.3);
                vy = -speed * 0.3;
            }
            vx = -speed;
        } else {
            // Portrait: spawn from top with angle
            y = -20;
            if (side === 'top') {
                x = Math.random() * (width * 0.3);
                vx = speed * 0.3;
            } else {
                x = width - Math.random() * (width * 0.3);
                vx = -speed * 0.3;
            }
            vy = speed;
        }

        this.addBullet(this.bulletPool.get(x, y, vx, vy));
    }

    spawnMultiDirectionalBullet(speed: number): void {
        const { width, height, isLandscape } = this.getCanvasSize();
        const direction = Math.floor(Math.random() * 4);

        let x: number, y: number, vx: number, vy: number;

        if (isLandscape) {
            // Landscape mode: primary direction is right->left
            switch (direction) {
                case 0: // Right (primary) - 다양한 각도 추가
                    x = width + 20;
                    y = Math.random() * height;
                    vx = -speed * (0.7 + Math.random() * 0.3);
                    vy = (Math.random() - 0.5) * speed * 0.8;
                    break;
                case 1: // Left
                    x = -20;
                    y = Math.random() * height;
                    vx = speed * (0.7 + Math.random() * 0.3);
                    vy = (Math.random() - 0.5) * speed * 0.8;
                    break;
                case 2: // Top
                    x = Math.random() * width;
                    y = -20;
                    vx = (Math.random() - 0.5) * speed * 0.8;
                    vy = speed * (0.7 + Math.random() * 0.3);
                    break;
                default: // Bottom
                    x = Math.random() * width;
                    y = height + 20;
                    vx = (Math.random() - 0.5) * speed * 0.8;
                    vy = -speed * (0.7 + Math.random() * 0.3);
                    break;
            }
        } else {
            // Portrait mode: primary direction is top->bottom
            switch (direction) {
                case 0: // Top (primary) - 다양한 각도 추가
                    x = Math.random() * width;
                    y = -20;
                    vx = (Math.random() - 0.5) * speed * 0.8;
                    vy = speed * (0.7 + Math.random() * 0.3);
                    break;
                case 1: // Bottom
                    x = Math.random() * width;
                    y = height + 20;
                    vx = (Math.random() - 0.5) * speed * 0.8;
                    vy = -speed * (0.7 + Math.random() * 0.3);
                    break;
                case 2: // Left
                    x = -20;
                    y = Math.random() * height;
                    vx = speed * (0.7 + Math.random() * 0.3);
                    vy = (Math.random() - 0.5) * speed * 0.8;
                    break;
                default: // Right
                    x = width + 20;
                    y = Math.random() * height;
                    vx = -speed * (0.7 + Math.random() * 0.3);
                    vy = (Math.random() - 0.5) * speed * 0.8;
                    break;
            }
        }

        this.addBullet(this.bulletPool.get(x, y, vx, vy));
    }

    spawnCircularPattern(speed: number): void {
        const { width, height } = this.getCanvasSize();
        // 화면 네 모서리에서 랜덤하게 선택하여 클러스터링 방지
        const corner = Math.floor(Math.random() * 4);
        let centerX: number, centerY: number;

        switch (corner) {
            case 0: // Top-left
                centerX = -50;
                centerY = -50;
                break;
            case 1: // Top-right
                centerX = width + 50;
                centerY = -50;
                break;
            case 2: // Bottom-left
                centerX = -50;
                centerY = height + 50;
                break;
            default: // Bottom-right
                centerX = width + 50;
                centerY = height + 50;
                break;
        }

        // Spawn 2-4 bullets in a spread pattern (더 적은 수로 조정)
        const count = 2 + Math.floor(Math.random() * 3);
        const angleOffset = Math.random() * Math.PI * 2;

        for (let i = 0; i < count; i++) {
            const angle = angleOffset + (i / count) * Math.PI * 0.6; // 더 좁은 범위
            const vx = Math.cos(angle) * speed;
            const vy = Math.sin(angle) * speed;

            this.addBullet(this.bulletPool.get(centerX, centerY, vx, vy));
        }
    }

    getBullets(): Bullet[] {
        return this.bullets;
    }

    transformPositionsOnOrientationChange(wasLandscape: boolean, isLandscape: boolean): void {
        // Get old and new canvas sizes
        const oldWidth = wasLandscape ? CONFIG.CANVAS_WIDTH_LANDSCAPE : CONFIG.CANVAS_WIDTH;
        const oldHeight = wasLandscape ? CONFIG.CANVAS_HEIGHT_LANDSCAPE : CONFIG.CANVAS_HEIGHT;
        const newWidth = isLandscape ? CONFIG.CANVAS_WIDTH_LANDSCAPE : CONFIG.CANVAS_WIDTH;
        const newHeight = isLandscape ? CONFIG.CANVAS_HEIGHT_LANDSCAPE : CONFIG.CANVAS_HEIGHT;

        for (const bullet of this.bullets) {
            // Normalize position (0-1 range)
            const normalizedX = bullet.x / oldWidth;
            const normalizedY = bullet.y / oldHeight;

            // Rotate 90 degrees based on orientation change direction
            if (wasLandscape && !isLandscape) {
                // Landscape → Portrait
                bullet.x = normalizedY * newWidth;
                bullet.y = (1 - normalizedX) * newHeight;
                // Rotate velocity
                const oldVx = bullet.vx;
                bullet.vx = bullet.vy;
                bullet.vy = -oldVx;
            } else {
                // Portrait → Landscape
                bullet.x = (1 - normalizedY) * newWidth;
                bullet.y = normalizedX * newHeight;
                // Rotate velocity
                const oldVx = bullet.vx;
                bullet.vx = -bullet.vy;
                bullet.vy = oldVx;
            }
        }
    }

    render(ctx: CanvasRenderingContext2D): void {
        for (const bullet of this.bullets) {
            bullet.render(ctx);
        }
    }
}
